import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import es from './locales/es.json';
import pt from './locales/pt.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
];

const STORAGE_KEY = 'smartkick_language';
const GEO_KEY = 'smartkick_language_geo_resolved';
const API_URL = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : `${window.location.origin}/api`;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      pt: { translation: pt },
    },
    fallbackLng: 'en',
    supportedLngs: ['en', 'es', 'pt'],
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      // No auto-cache — only LanguageSwitcher and resolveLanguageFromGeo
      // write to localStorage, so the geo fallback fires reliably on first visit.
      caches: [],
      lookupLocalStorage: STORAGE_KEY,
    },
    react: { useSuspense: false },
  });

/**
 * Resolve the visitor's language from server-side geo (Cloudflare country
 * header / IP lookup) on first ever visit. Subsequent visits use the
 * user's explicit choice from localStorage.
 *
 * Idempotent: writes a `GEO_KEY` flag so we never override a returning
 * visitor (even one who switched back to English after we auto-picked
 * Spanish — we respect their choice).
 */
export async function resolveLanguageFromGeo() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const alreadyResolved = localStorage.getItem(GEO_KEY);
    // If user has already chosen, or we've already geo-resolved once, skip.
    if (stored || alreadyResolved) return;

    const resp = await fetch(`${API_URL}/geo/language`, { credentials: 'omit' });
    if (!resp.ok) return;
    const data = await resp.json();
    const detected = data?.language;
    if (detected && ['en', 'es', 'pt'].includes(detected)) {
      // Only apply if it differs from what i18next defaulted to
      if (i18n.language?.split('-')[0] !== detected) {
        await i18n.changeLanguage(detected);
      }
      // Persist so the LanguageSwitcher reflects the auto-pick & we don't refetch.
      localStorage.setItem(STORAGE_KEY, detected);
      localStorage.setItem(GEO_KEY, JSON.stringify({ country: data.country, source: data.source, at: Date.now() }));
    }
  } catch {
    // Network failure → silently keep browser-language default. Not a blocker.
  }
}

export default i18n;
